/**
 * Matches a date-only ISO 8601 string (YYYY-MM-DD) with no time component.
 */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Safely parses a date string and returns a valid Date or null.
 * Returns null if the date string is missing, empty, or invalid.
 *
 * Date-only strings (YYYY-MM-DD) are interpreted as local midnight rather
 * than UTC midnight. This avoids a timezone shift that would otherwise cause
 * the displayed calendar date to differ from the API value (e.g. "2026-05-05"
 * rendering as 2026-05-04 in Pacific timezones). Per the ECMAScript spec,
 * appending a time component without an offset switches parsing to local time.
 *
 * > When the time zone offset is absent, date-only forms are interpreted as a UTC time and date-time forms are interpreted as a local time.
 *
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
 *
 * @param dateString - The date string to parse
 * @returns A valid Date object, or null if the input is invalid
 */
export function parseDate(dateString: string | undefined | null): Date | null {
  if (!dateString) {
    return null;
  }
  const normalized = DATE_ONLY_PATTERN.test(dateString) ? `${dateString}T00:00:00` : dateString;
  const date = new Date(normalized);
  // Check if the date is valid (Invalid Date has NaN as time value)
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}
